// Supplier/Enterprise dashboard for manufacturer data requests (/manufacturers).
// Create requests (pick products + flag questions), track their status, and
// review + apply the manufacturer's answers into LCA assessments.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import RequestModal from '../components/manufacturer/RequestModal';
import ReviewDrawer from '../components/manufacturer/ReviewDrawer';
import { MANUFACTURER_REQUESTS_LAUNCHED } from '../config/featureFlags';
import { useConfirm } from '../components/ConfirmDialog';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';
const FONT = "'Raleway', 'SF Pro Display', sans-serif";

const STATUS_STYLES = {
  sent: { label: 'Sent', bg: '#eef2ff', color: '#4338ca' },
  viewed: { label: 'Viewed', bg: '#e0f2fe', color: '#0369a1' },
  in_progress: { label: 'In progress', bg: '#fef9c3', color: '#a16207' },
  submitted: { label: 'Submitted', bg: '#dcfce7', color: '#15803d' },
  applied: { label: 'Applied', bg: '#ecfdf5', color: '#047857' },
  expired: { label: 'Expired', bg: '#f3f4f6', color: '#6b7280' },
  revoked: { label: 'Revoked', bg: '#fef2f2', color: '#b91c1c' },
};

export default function ManufacturerRequests() {
  const { supplierInfo, getToken, isStaff } = useAuth();
  const { confirm } = useConfirm();
  const supplierId = supplierInfo?.supplier_id;
  const [searchParams, setSearchParams] = useSearchParams();

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [billing, setBilling] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showUpsell, setShowUpsell] = useState(false);
  const [reviewRequestId, setReviewRequestId] = useState(searchParams.get('request') || null);
  const [actionBusy, setActionBusy] = useState({});
  const [toast, setToast] = useState(null);

  const authHeaders = useCallback(async () => {
    const token = await getToken?.();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [getToken]);

  const loadRequests = useCallback(async () => {
    try {
      const headers = await authHeaders();
      const res = await axios.get(`${API_BASE_URL}/manufacturer-requests`, { headers });
      setRequests(res.data?.data || []);
      setError(null);
    } catch (e) {
      // 402 = plan without the feature; the page still renders with an upsell CTA.
      if (e?.response?.status === 402) {
        setRequests([]);
        setError(null);
      } else {
        setError(e?.response?.data?.error || e.message || 'Failed to load requests');
      }
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    if (!supplierId) return;
    loadRequests();
    (async () => {
      try {
        const headers = await authHeaders();
        const res = await axios.get(`${API_BASE_URL}/billing/status`, { headers });
        setBilling(res.data?.data || null);
      } catch { /* non-blocking */ }
    })();
  }, [supplierId, loadRequests, authHeaders]);

  const canRequest = billing ? !!billing.can_request_manufacturer_data : true;

  const showToast = (message) => {
    setToast(message);
    setTimeout(() => setToast(null), 2600);
  };

  const openReview = (id) => {
    setReviewRequestId(id);
    setSearchParams({ request: id }, { replace: true });
  };
  const closeReview = () => {
    setReviewRequestId(null);
    setSearchParams({}, { replace: true });
  };

  const copyLink = async (request) => {
    setActionBusy((b) => ({ ...b, [request.id]: 'copy' }));
    try {
      const headers = await authHeaders();
      const res = await axios.get(`${API_BASE_URL}/manufacturer-requests/${request.id}`, { headers });
      const d = res.data?.data;
      await navigator.clipboard.writeText(`${d.portal_url}\nAccess code: ${d.access_code}`);
      showToast('Link + access code copied');
    } catch (e) {
      showToast(e?.response?.data?.error || 'Copy failed');
    } finally {
      setActionBusy((b) => ({ ...b, [request.id]: null }));
    }
  };

  const remind = async (request) => {
    const ok = await confirm({
      title: 'Resend this request?',
      message: `We'll email ${request.manufacturer_email} again with a fresh access code. Any code they already have will stop working.`,
      confirmLabel: 'Resend email',
    });
    if (!ok) return;
    setActionBusy((b) => ({ ...b, [request.id]: 'remind' }));
    try {
      const headers = await authHeaders();
      const res = await axios.post(`${API_BASE_URL}/manufacturer-requests/${request.id}/remind`, {}, { headers });
      showToast(res.data?.data?.email_sent ? 'Reminder sent' : 'Code refreshed — email not configured, use Copy link');
      loadRequests();
    } catch (e) {
      showToast(e?.response?.data?.error || 'Reminder failed');
    } finally {
      setActionBusy((b) => ({ ...b, [request.id]: null }));
    }
  };

  const revoke = async (request) => {
    const ok = await confirm({
      title: 'Cancel this request?',
      message: `${request.manufacturer_email} will no longer be able to open the link. You can send them a new request at any time.`,
      confirmLabel: 'Cancel request',
      cancelLabel: 'Keep it open',
      tone: 'danger',
    });
    if (!ok) return;
    setActionBusy((b) => ({ ...b, [request.id]: 'revoke' }));
    try {
      const headers = await authHeaders();
      await axios.post(`${API_BASE_URL}/manufacturer-requests/${request.id}/revoke`, {}, { headers });
      showToast('Request revoked');
      loadRequests();
    } catch (e) {
      showToast(e?.response?.data?.error || 'Revoke failed');
    } finally {
      setActionBusy((b) => ({ ...b, [request.id]: null }));
    }
  };

  const knownContacts = useMemo(() => {
    const seen = new Map();
    for (const r of requests) {
      if (r.manufacturer_email && !seen.has(r.manufacturer_email)) {
        seen.set(r.manufacturer_email, { email: r.manufacturer_email, name: r.manufacturer_name || '' });
      }
    }
    return [...seen.values()];
  }, [requests]);

  // Shipped dark: until launch, direct URL access bounces everyone but staff.
  if (!MANUFACTURER_REQUESTS_LAUNCHED && supplierInfo && !isStaff) {
    return <Navigate to="/" replace />;
  }

  if (!supplierId) {
    return (
      <div style={{ padding: 40, fontFamily: FONT, color: '#6b7280', textAlign: 'center' }}>
        You need to be signed in to view this page.
      </div>
    );
  }

  return (
    <div style={{ background: '#f8f9fb', minHeight: 'calc(100vh - 59px)', fontFamily: FONT }}>
      <div className="container-fluid px-4 py-4" style={{ maxWidth: 1280 }}>

        {/* Header */}
        <div className="d-flex align-items-end justify-content-between flex-wrap gap-3 mb-4">
          <div>
            <div className="text-uppercase small fw-bold" style={{ color: '#6b7280', letterSpacing: 0.5 }}>Manufacturers</div>
            <h3 className="fw-bold mb-1" style={{ color: '#111827' }}>Data requests</h3>
            <div style={{ color: '#6b7280', fontSize: 13, maxWidth: 640 }}>
              Ask your manufacturers to fill in the production details you're missing — they get an email link and a code, no account needed.
            </div>
          </div>
          <button
            className="btn"
            onClick={() => (canRequest ? setShowModal(true) : setShowUpsell(true))}
            style={{ background: '#111827', color: '#fff', borderRadius: 10, padding: '10px 20px', fontWeight: 600 }}
          >
            + New request
          </button>
        </div>

        {/* List */}
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
          <div
            className="d-none d-md-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: '1.4fr 1.6fr 130px 150px 110px 210px',
              background: '#f9fafb',
              borderBottom: '1px solid #e5e7eb',
              fontSize: 12,
              fontWeight: 600,
              color: '#6b7280',
              textTransform: 'uppercase',
              letterSpacing: 0.4,
            }}
          >
            <HCell>Manufacturer</HCell>
            <HCell>Products</HCell>
            <HCell>Answers</HCell>
            <HCell>Last activity</HCell>
            <HCell>Status</HCell>
            <HCell>Actions</HCell>
          </div>

          {loading && <Empty text="Loading requests…" />}
          {!loading && error && <Empty text={error} color="#b91c1c" />}
          {!loading && !error && requests.length === 0 && (
            <div className="text-center py-5 px-3">
              <div style={{ fontSize: 36, marginBottom: 8 }}>📨</div>
              <div style={{ fontWeight: 600, color: '#111827', marginBottom: 4 }}>No requests yet</div>
              <div style={{ color: '#6b7280', fontSize: 13, maxWidth: 460, margin: '0 auto 16px' }}>
                Send your first request to a manufacturer — pick products, choose which inputs you need
                (processes, materials, packaging, energy…), and they fill in the rest.
              </div>
              <button
                className="btn btn-sm"
                onClick={() => (canRequest ? setShowModal(true) : setShowUpsell(true))}
                style={{ background: '#111827', color: '#fff', borderRadius: 8, fontWeight: 600 }}
              >
                + New request
              </button>
            </div>
          )}

          {requests.map((r, idx) => {
            const style = STATUS_STYLES[r.status] || STATUS_STYLES.sent;
            const busy = actionBusy[r.id];
            const lastActivity = r.submitted_at || r.reminded_at || r.first_viewed_at || r.sent_at || r.created_at;
            return (
              <div
                key={r.id}
                className="d-grid align-items-center"
                style={{
                  gridTemplateColumns: '1.4fr 1.6fr 130px 150px 110px 210px',
                  borderBottom: idx === requests.length - 1 ? 'none' : '1px solid #f3f4f6',
                }}
              >
                <Cell>
                  <div style={{ fontWeight: 600, color: '#111827', fontSize: 13 }}>{r.manufacturer_name || '—'}</div>
                  <div style={{ color: '#9ca3af', fontSize: 12 }}>{r.manufacturer_email}</div>
                </Cell>
                <Cell>
                  <div style={{ fontSize: 13, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {(r.products || []).map((p) => p.name).filter(Boolean).join(', ') || '—'}
                  </div>
                  <div style={{ color: '#9ca3af', fontSize: 12 }}>{(r.products || []).length} product{(r.products || []).length === 1 ? '' : 's'}</div>
                </Cell>
                <Cell>
                  <span style={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>
                    {r.questions_answered}/{r.questions_total}
                  </span>
                  {r.questions_applied > 0 && (
                    <div style={{ fontSize: 11, color: '#047857' }}>{r.questions_applied} applied</div>
                  )}
                </Cell>
                <Cell>
                  <span style={{ fontSize: 12, color: '#6b7280' }}>{formatDate(lastActivity)}</span>
                </Cell>
                <Cell>
                  <span style={{ background: style.bg, color: style.color, fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 99, whiteSpace: 'nowrap' }}>
                    {style.label}
                  </span>
                </Cell>
                <Cell>
                  <div className="d-flex gap-1 flex-wrap">
                    <MiniBtn onClick={() => openReview(r.id)} primary={r.status === 'submitted'}>
                      {r.status === 'submitted' ? 'Review' : 'View'}
                    </MiniBtn>
                    <MiniBtn onClick={() => copyLink(r)} disabled={busy === 'copy' || !!r.revoked_at}>
                      {busy === 'copy' ? '…' : 'Copy link'}
                    </MiniBtn>
                    {!r.submitted_at && !r.revoked_at && (
                      <MiniBtn onClick={() => remind(r)} disabled={busy === 'remind'}>
                        {busy === 'remind' ? '…' : 'Remind'}
                      </MiniBtn>
                    )}
                    {!r.revoked_at && !r.submitted_at && (
                      <MiniBtn onClick={() => revoke(r)} disabled={busy === 'revoke'} danger>
                        Revoke
                      </MiniBtn>
                    )}
                  </div>
                </Cell>
              </div>
            );
          })}
        </div>
      </div>

      {toast && (
        <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: '#111827', color: '#fff', padding: '10px 20px', borderRadius: 10, fontSize: 13, fontWeight: 500, zIndex: 3000, boxShadow: '0 4px 14px rgba(0,0,0,0.25)' }}>
          {toast}
        </div>
      )}

      {showModal && (
        <RequestModal
          supplierId={supplierId}
          authHeaders={authHeaders}
          knownContacts={knownContacts}
          onClose={() => setShowModal(false)}
          onCreated={() => { loadRequests(); }}
        />
      )}

      {reviewRequestId && (
        <ReviewDrawer
          requestId={reviewRequestId}
          supplierId={supplierId}
          authHeaders={authHeaders}
          onClose={closeReview}
          onChanged={loadRequests}
        />
      )}

      {showUpsell && (
        <div className="modal d-block" style={{ background: 'rgba(17,24,39,0.5)' }} onClick={() => setShowUpsell(false)}>
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content" style={{ borderRadius: 14, fontFamily: FONT }}>
              <div className="modal-body text-center p-4">
                <div style={{ fontSize: 36, marginBottom: 8 }}>🔓</div>
                <h5 className="fw-bold" style={{ color: '#111827' }}>Manufacturer requests are a paid feature</h5>
                <p style={{ color: '#6b7280', fontSize: 14 }}>
                  Upgrade to send data requests to your manufacturers — they answer through a secure link, no account needed.
                </p>
                <div className="d-flex justify-content-center gap-2">
                  <Link to="/billing" className="btn" style={{ background: '#111827', color: '#fff', borderRadius: 8, fontWeight: 600 }}>
                    See plans
                  </Link>
                  <button className="btn btn-outline-secondary" style={{ borderRadius: 8 }} onClick={() => setShowUpsell(false)}>
                    Not now
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function HCell({ children }) {
  return <div style={{ padding: '10px 14px' }}>{children}</div>;
}
function Cell({ children }) {
  return <div style={{ padding: '12px 14px', minWidth: 0 }}>{children}</div>;
}
function Empty({ text, color = '#9ca3af' }) {
  return <div className="text-center py-5" style={{ color, fontSize: 14 }}>{text}</div>;
}
function MiniBtn({ children, onClick, disabled, danger, primary }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="btn btn-sm"
      style={{
        background: primary ? '#111827' : '#fff',
        color: primary ? '#fff' : danger ? '#b91c1c' : '#374151',
        border: `1px solid ${danger ? '#fecaca' : '#e5e7eb'}`,
        borderRadius: 7,
        fontSize: 12,
        fontWeight: 500,
        padding: '3px 10px',
        opacity: disabled ? 0.6 : 1,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}
